import { useMemo, useState } from "react";
import {
  AiEyebrow,
  Card,
  C,
  FONT,
  SCENARIOS,
  SectionTitle,
  Slider,
  Waterfall,
} from "./shared";

export function Scenarios() {
  const [selected, setSelected] = useState("expected");
  const [priceReduction, setPriceReduction] = useState(6);
  const [volumeChange, setVolumeChange] = useState(0);
  const [paymentTerms, setPaymentTerms] = useState(75);
  const model = useMemo(() => {
    const current = 32.4;
    const landing = current * (1 - priceReduction / 100);
    const saving = Math.round(
      (current - landing) * 27557 * (1 + volumeChange / 100)
    );
    const workingCapital = Math.round(
      ((paymentTerms - 60) / 30) * 810000 * 0.041
    );
    return { landing, saving, workingCapital, value: saving * 3 };
  }, [paymentTerms, priceReduction, volumeChange]);
  const scenario =
    SCENARIOS.find((item) => item.key === selected) || SCENARIOS[1];
  const formatMoney = (value) => `$${value.toLocaleString("en-US")}`;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div
        style={{
          background: C.summaryBg,
          borderLeft: `3px solid ${C.summaryBorder}`,
          borderRadius: "0 8px 8px 0",
          padding: 20,
        }}
      >
        <AiEyebrow label="AI recommendation" color={C.purple3} />
        <p
          style={{
            margin: "6px 0 0",
            color: C.slate3,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          At the current settings the deal lands at {scenario.price}, worth{" "}
          {formatMoney(model.saving)} a year plus{" "}
          {formatMoney(model.workingCapital)} of working-capital benefit.
          Selected scenario: {scenario.title} at {scenario.price} with{" "}
          {scenario.pct} likelihood.
        </p>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,1fr)",
          gap: 16,
        }}
        className="np-3col"
      >
        {SCENARIOS.map((item) => (
          <button
            type="button"
            key={item.key}
            onClick={() => setSelected(item.key)}
            style={{
              textAlign: "left",
              cursor: "pointer",
              background: selected === item.key ? "#F5F3FF" : C.white,
              border:
                selected === item.key
                  ? `2px solid ${C.purple}`
                  : `1px solid ${C.border2}`,
              borderRadius: 12,
              padding: 20,
              fontFamily: FONT,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                color: C.deep,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {item.title}
              <span style={{background: "rgb(241, 245, 249)", color: "rgb(100, 116, 139)", borderRadius:"4px", padding: "2px 8px", fontSize: 11, fontWeight: 600}}>{item.pct}</span>
            </div>
            <div
              style={{
                color: C.deep,
                fontSize: 24,
                fontWeight: 800,
                margin: "8px 0",
              }}
            >
              {item.price}
            </div>
            <div style={{ color: C.slate2, fontSize: 12 }}>{item.saves}</div>
            <div style={{ color: C.gray, fontSize: 12, marginTop: 6 }}>
              {item.detail}
            </div>
          </button>
        ))}
      </div>
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 8,
          }}
        >
          <SectionTitle size={16}>
            Savings waterfall — current price to target
          </SectionTitle>
          <AiEyebrow label="AI attributed" color={C.purple3} />
        </div>
        <Waterfall />
      </Card>
      <div
        style={{ display: "grid", gridTemplateColumns: "0.8fr 1.2fr", gap: 24 }}
        className="np-2col"
      >
        <div
          style={{
            background: `linear-gradient(150deg,${C.deep},#3A2A6B)`,
            borderRadius: 12,
            padding: 24,
            color: "#fff",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              color: "rgba(255,255,255,.6)",
            }}
          >
            Modelled Landing Price
          </div>
          <div style={{ fontSize: 48, fontWeight: 800, margin: "8px 0" }}>
            ${model.landing.toFixed(2)}
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,.7)" }}>
            from $32.40 · benchmark $29.35
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px 16px",
              marginTop: 16,
              fontSize: 12,
            }}
          >
            Annual saving {formatMoney(model.saving)} · Working capital{" "}
            {formatMoney(model.workingCapital)} · 36-month value{" "}
            {formatMoney(model.value)}
          </div>
        </div>
        <Card>
          <strong style={{ color: C.ink2 }}>Negotiation simulator</strong>
          <Slider
            label="Price reduction"
            value={priceReduction}
            min={0}
            max={12}
            step={0.5}
            onChange={setPriceReduction}
            display={`${priceReduction}%`}
          />
          <Slider
            label="Volume change"
            value={volumeChange}
            min={-20}
            max={30}
            step={1}
            onChange={setVolumeChange}
            display={`${volumeChange > 0 ? "+" : ""}${volumeChange}%`}
          />
          <Slider
            label="Payment terms"
            value={paymentTerms}
            min={30}
            max={120}
            step={15}
            onChange={setPaymentTerms}
            display={`Net ${paymentTerms}`}
          />
        </Card>
      </div>
    </div>
  );
}
